'use client';

import {
  type AutocompleteParams,
  getAutocompleteFn,
  getAddressFn,
  type Address,
  type GoogleAddressParams,
} from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { FloatingLabelInput } from '@ahm/react-ui/components/floating-label-fields';
import {
  Box,
  Button,
  CloseButton,
  Combobox,
  Group,
  Loader,
  Text,
  TextInput,
  rem,
  useCombobox,
} from '@mantine/core';
import '@mantine/core/styles.layer.css';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import classes from '../address-autocomplete.module.css';
import { createTranslations, locales } from '../locales';
import {
  CurrentAddressOptions,
  FrequentAddressOptions,
  SearchAddressOptions,
} from '../options';
import PinMapItem from '../options/pin-map-item';
import type { BaseAddressAutocompleteProps } from '../types';
import { genBuildFields } from '../utils';
import ActionMenuAutocomplete from './action-autocomplete.menu';
import useAutocompleteIcon from './use-autocomplete-icon';
import useAutocompleteText from './use-autocomplete-text';
import GgSearchIcon from '@/assets/icons/gg-search-icon.svg';
import { QuickAccessAIChatButton } from '@/features/AI';
import { ActionIndexPath } from '@/lib/constants';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function BaseAddressAutocomplete({
  pointType,
  pointIndex,
  lang = 'vi',
  placeholder,
  inputProps = {},
  inputStyles = {},
  address: initAddress,
  opened,
  onChange,
  children,
  childOptions = [],
  savedPlaceSlots = undefined,
  isCurrentLocation = true,
  isMobile = false,
  isPinLocation = false,
  isFloatLabel = false,
  pathIndex,
  ...props
}: BaseAddressAutocompleteProps) {
  const t = createTranslations({ locales, lang });

  const combobox = useCombobox({
    opened,
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [loading, setLoading] = useState(false);
  const [addressOptions, setAddressOptions] = useState<Address[] | null>(null);
  const [value, setValue] = useState(initAddress?.address ?? '');
  const [debouncedValue] = useDebouncedValue(value, 800);
  const [empty, setEmpty] = useState(false);
  const [searchByGPlaces, setSearchByGPlaces] = useState(false);

  const abortController = useRef<AbortController | null>(null);
  const frequentLocations = useLocationStore.use.frequentLocations();
  const savedLocations = useLocationStore.use.savedLocations();
  const selectedSavedPlace = useLocationStore.use.selectedSavedPlace();
  const setSelectedSavedPlace = useLocationStore.use.setSelectedSavedPlace();
  const addToFrequentLocations = useLocationStore.use.addToFrequentLocations();
  const selectedPinMap = useLocationStore.use.selectedPinMap();
  const setSelectedPinMap = useLocationStore.use.setSelectedPinMap();
  const pickupLocation = useLocationStore.use.paths()[ActionIndexPath.Pickup];

  const { inputPlaceholder } = useAutocompleteText({
    lang,
    pointType,
    placeholder,
  });

  const icon = useAutocompleteIcon({ pointIndex, pointType });

  useEffect(() => {
    const existedValue = addressOptions?.some(
      (item) => item.address === debouncedValue
    );

    if (existedValue) {
      return;
    }

    fetchOptions(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useEffect(() => {
    if (selectedSavedPlace?.address) {
      handleOptionSubmit(selectedSavedPlace.address);
      setSelectedSavedPlace(null);
    }

    if (selectedPinMap) {
      handleSelectAddress(selectedPinMap);
      setSelectedPinMap(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSavedPlace, selectedPinMap]);

  const fetchOptions = (query: string, mode?: string) => {
    const params: AutocompleteParams = {
      input: query,
      mode,
    };

    if (!query || query.length < 2) {
      setLoading(false);
      if (!query || query.length < 1) setAddressOptions(null);
      return;
    }

    abortController.current?.abort();
    abortController.current = new AbortController();
    setLoading(true);

    if (pickupLocation?.lat && pickupLocation?.lng) {
      params.lat = pickupLocation.lat;
      params.lng = pickupLocation.lng;
    }

    getAutocompleteFn(params, { signal: abortController.current.signal })
      .then((result) => {
        // Flatten the entrances into the parent array
        const flatAddressWithEntrance = result.flatMap((item) => {
          const mappedEntrances =
            item.entrances?.map((entrance) => ({
              ...item,
              // address: entrance.name,
              address: `${entrance.short_name}, ${item.second_label}`,

              lat: entrance.coordinates[0],
              lng: entrance.coordinates[1],
              first_label: entrance.name,
              second_label: entrance.short_name,
              isEntrance: true,
              polygon: item.polygon,
              entrances: item.entrances,
            })) ?? [];

          return [item, ...mappedEntrances];
        }) as Address[];

        setAddressOptions(flatAddressWithEntrance);
        setLoading(false);
        setEmpty(result.length === 0);
        abortController.current = null;
      })
      .catch(() => {
        setAddressOptions([]);
        setLoading(false);
        setEmpty(true);
        abortController.current = null;
      });
  };

  const handleOptionSubmit = (optionValue: string) => {
    const search = addressOptions ?? [];
    const frequent = frequentLocations ?? [];
    const saved = savedLocations ?? [];
    const customs = childOptions ?? [];

    const combine = [...search, ...saved, ...frequent, ...customs];
    const selectedOption =
      combine?.find((item) => item.address === optionValue) ?? null;

    if (!selectedOption) {
      return;
    }

    void addToFrequentLocations(selectedOption);
    if (
      (!selectedOption.lat || !selectedOption.lng) &&
      !!selectedOption.place_id
    ) {
      getAddressFn({
        place_id: selectedOption.place_id,
        sessiontoken: selectedOption.sessiontoken,
        address: selectedOption.address || '',
      } as GoogleAddressParams & { address?: string })
        .then((res: Address | null) => {
          if (res) {
            if (!res.lat || !res.lng) return;

            const newAddress = {
              ...selectedOption,
              lat: res.lat,
              lng: res.lng,
            };

            setAddressOptions([newAddress]);
            handleSelectAddress(newAddress);
          }
        })
        .catch((err) => {
          console.error('Error fetching address details:', err);
        });

      return;
    }

    handleSelectAddress(selectedOption);
  };

  const handleSelectAddress = (addr: Address) => {
    setValue(addr.address);
    handleOnChange(addr);
    combobox.closeDropdown();
  };

  const handleOnChange = (addr: Address | null) => {
    if (!addr) {
      onChange(null);
      return;
    }

    const item = genBuildFields(addr, [
      'address',
      'lat',
      'lng',
      'first_label',
      'second_label',
      'address_detail',
      'code',
      'contact_name',
      'contact_number',
      'feature_type',
      'adr_source',
      'entrances',
      'polygon',
    ]);
    onChange(item);
  };

  return (
    <>
      <Combobox
        offset={isMobile ? 1 : 0}
        onOptionSubmit={handleOptionSubmit}
        withinPortal={true}
        store={combobox}
        position="bottom"
        {...props}
        styles={{
          dropdown: {
            filter: isMobile
              ? 'none'
              : 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.16))',
            border: 'none',
            borderRadius: `0 0 ${rem(12)} ${rem(12)}`,
            overflow: 'hidden',

            left: isMobile ? 0 : 'auto',
            height: isMobile ? 'calc(100svh - 96px)' : 'auto',
          },
          options: {
            maxHeight: isMobile ? '100%' : rem(325),
            height: isMobile ? '100%' : 'auto',
            overflowY: 'auto',
            overflowX: 'hidden',
          },
          option: { padding: `${rem(12)} ${rem(16)}` },
          empty: { padding: `${rem(12)} ${rem(16)}` },
        }}
      >
        <Combobox.Target>
          {!isFloatLabel ? (
            <TextInput
              size="md"
              variant="outline"
              // data-autofocus
              placeholder={inputPlaceholder}
              value={value}
              onChange={(event) => {
                setValue(event.currentTarget.value);
                combobox.resetSelectedOption();
                combobox.openDropdown();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => {
                combobox.openDropdown();
                if (addressOptions === null) {
                  fetchOptions(value);
                }
              }}
              onBlur={() => combobox.closeDropdown()}
              leftSection={icon}
              rightSection={
                <Group wrap="nowrap" gap={0}>
                  {loading ? (
                    <Loader size={20} color="neutral.4" />
                  ) : debouncedValue ? (
                    <CloseButton
                      size="md"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setValue('');
                        handleOnChange(null);
                        setSearchByGPlaces(false);
                      }}
                      aria-label="Clear value"
                    />
                  ) : null}
                  {!isMobile ? (
                    <ActionMenuAutocomplete
                      pathIndex={pathIndex}
                      pointType={pointType}
                      lang={lang}
                      handleSelectAddress={handleSelectAddress}
                      isPinLocation={isPinLocation}
                    />
                  ) : null}
                </Group>
              }
              styles={inputStyles}
              leftSectionWidth={icon ? rem(48) : 0}
              rightSectionWidth={rem(
                (!isMobile ? 48 + 4 : 24) + (debouncedValue || loading ? 24 : 0)
              )}
              {...inputProps}
            />
          ) : (
            <FloatingLabelInput
              size="md"
              variant="outline"
              // data-autofocus
              placeholder={inputPlaceholder}
              value={value}
              onChange={(event) => {
                setValue(event.currentTarget.value);
                combobox.resetSelectedOption();
                combobox.openDropdown();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => {
                combobox.openDropdown();
                if (addressOptions === null) {
                  fetchOptions(value);
                }
              }}
              onBlur={() => combobox.closeDropdown()}
              leftSection={icon}
              rightSection={
                !isMobile ? (
                  <Group wrap="nowrap" gap={0}>
                    {loading ? (
                      <Loader size={20} color="neutral.4" />
                    ) : debouncedValue ? (
                      <CloseButton
                        size="md"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setValue('');
                          handleOnChange(null);
                          setSearchByGPlaces(false);
                        }}
                        aria-label="Clear value"
                      />
                    ) : null}
                    <ActionMenuAutocomplete
                      lang={lang}
                      handleSelectAddress={handleSelectAddress}
                      isPinLocation={isPinLocation}
                    />
                  </Group>
                ) : null
              }
              styles={inputStyles}
              leftSectionWidth={icon ? rem(48) : 0}
              rightSectionWidth={rem(
                48 + 4 + (debouncedValue || loading ? 24 : 0)
              )}
              {...inputProps}
            />
          )}
        </Combobox.Target>

        <Combobox.Dropdown p={0}>
          <Combobox.Options classNames={{ options: classes.options }}>
            {debouncedValue ? (
              <Box bg="white" mb={isMobile ? 60 : 0}>
                {empty ? (
                  <>
                    <Combobox.Empty>{t('no_results')}</Combobox.Empty>
                  </>
                ) : (
                  <SearchAddressOptions
                    currentInputValue={value}
                    options={addressOptions}
                  />
                )}

                {!searchByGPlaces ? (
                  <>
                    <Button
                      c="neutral.6"
                      fullWidth
                      justify="flex-start"
                      // my={rem(16)}
                      h={rem(60)}
                      onClick={() => {
                        fetchOptions(value, 'google');
                        setSearchByGPlaces(true);
                      }}
                      variant="transparent"
                      bg="blue.0"
                      leftSection={<GgSearchIcon />}
                      styles={{
                        label: {
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      }}
                    >
                      <Text lineClamp={2} fw={600} fz={14}>
                        Hiển thị thêm kết quả tìm kiếm: "{value}"{' '}
                      </Text>
                    </Button>
                    <QuickAccessAIChatButton
                      fullWidth
                      radius={0}
                      styles={{
                        root: {
                          height: rem(60),
                          justifyContent: 'flex-start',
                        },
                        inner: {
                          gap: rem(8),
                        },
                        label: {
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      }}
                      text={
                        <Group wrap="nowrap" gap={4}>
                          <Text size="sm">Lên đơn với AI giao hàng đến</Text>
                          {value ? (
                            <Text size="sm" fw={700}>
                              "{value}"
                            </Text>
                          ) : null}
                        </Group>
                      }
                    />
                  </>
                ) : null}
              </Box>
            ) : (
              <>
                <Box bg="white">
                  {isCurrentLocation ? (
                    <CurrentAddressOptions
                      currentInputValue={value}
                      lang={lang}
                      handleSelectAddress={handleSelectAddress}
                    />
                  ) : null}
                  {isMobile ? (
                    <QuickAccessAIChatButton
                      styles={{
                        root: {
                          padding: `${rem(12)} ${rem(16)}`,
                          justifyContent: 'flex-start',
                          height: 'auto',
                          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px 0px',
                        },
                        inner: {
                          gap: rem(8),
                        },
                      }}
                    />
                  ) : null}
                </Box>
                {savedPlaceSlots}
                <FrequentAddressOptions
                  lang={lang}
                  currentInputValue={value}
                  options={frequentLocations}
                />
              </>
            )}

            {children}

            {isMobile ? <PinMapItem lang={lang} pointType={pointType} /> : null}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </>
  );
}
